import { ActionCreator } from "@reduxjs/toolkit";
import { useCallback, useRef } from "react";
import {
  TypedUseSelectorHook,
  // eslint-disable-next-line no-restricted-imports
  useDispatch as baseUseDispatch,
  // eslint-disable-next-line no-restricted-imports
  useSelector as baseUseSelector,
  // eslint-disable-next-line no-restricted-imports
  useStore as baseUseStore,
} from "react-redux";

import { USE_SELECTOR_LOGGING } from "./config";
import type { AppAction, AppDispatch, RootState, createStore } from "./store";
import { useSelectorWithLogging } from "./utils/useSelectorWithLogging";

export type RootStore = ReturnType<typeof createStore>;
export type { RootState };

// App-specific typed versions of the common react-redux hooks
export const useStore: () => RootStore = baseUseStore;
export const useDispatch: () => AppDispatch = baseUseDispatch;
export const useSelector: TypedUseSelectorHook<RootState> =
  USE_SELECTOR_LOGGING && process.env.NODE_ENV === "development"
    ? useSelectorWithLogging
    : baseUseSelector;

/** Simple wrapper around `useDispatch` into an action creator. */
export function useDispatchAction<A extends ActionCreator<AppAction>>(
  actionCreator: A,
): (...args: Parameters<A>) => void {
  const dispatch = useDispatch();
  return useCallback(
    (...args: Parameters<A>) => dispatch(actionCreator(...args)),
    [dispatch, actionCreator],
  );
}

/** Allows for evaluating a selector without re-rendering or subscribing to the store. */
export function useLazySelector<R, Args extends unknown[] = []>(
  selector: (state: RootState, ...args: Args) => R,
): (...args: Args) => R {
  const store = useStore();
  const latestSelector = useRef(selector);
  latestSelector.current = selector;
  return useCallback(
    (...args: Args) => latestSelector.current(store.getState(), ...args),
    [latestSelector, store],
  );
}
